import { RecordOutcome } from "./RecordOutcome.js";
import {
  Fragment,
  useEffect,
  useId,
  useRef,
  useState,
  type CSSProperties,
  type FormEvent,
  type ReactNode,
} from "react";

import type { DataQueryRun, PolyfutureApiClient } from "./api.js";
import { formatExposureSize, productLabel } from "./product-language.js";
import { InvestingHelp } from "./Help.js";
import { ProvenanceDrawer } from "./Provenance.js";
import {
  ProductHeading,
  ProductPage,
  ProductPanel,
  ProgressiveDisclosure,
  StatusDot,
} from "./ProductSurface.js";
import type { ActiveView } from "./routes.js";
import type {
  CurrentUser,
  BookRecord,
  MechanismProjection,
  MechanismVersionDiff,
  RunProgress,
  ScenarioDraftInput,
  ScenarioPolarity,
  ScenarioCatalogRecord,
  ScenarioExposureMatrix,
  ScenarioRecord,
  ScenarioVersionSummary,
  WorkspaceData,
} from "./types.js";

export function BookSectionNav({
  activeView,
  onNavigate,
}: {
  activeView: ActiveView;
  onNavigate: (view: ActiveView) => void;
}) {
  const items: { view: ActiveView; label: string }[] = [
    { view: "workspace", label: "Overview" },
    { view: "positions", label: "Holdings" },
    { view: "assumptions", label: "Investment assumptions" },
    { view: "evidence", label: "Alerts" },
    { view: "decision_packs", label: "Reports" },
  ];
  const selected = parentView(activeView);
  return (
    <nav className="book-section-nav pf-portfolio-navigation" aria-label="Portfolio workspace">
      <div className="pf-primary-tabs">
        {items.map((item) => (
          <button
            type="button"
            key={item.view}
            className={selected === item.view ? "selected" : ""}
            aria-current={selected === item.view ? "page" : undefined}
            onClick={() => onNavigate(item.view)}
          >
            {item.label}
          </button>
        ))}
      </div>
    </nav>
  );
}

export function PositionsView({
  data,
  selectedId,
  onOpen,
  onBack,
}: {
  data: WorkspaceData;
  selectedId?: string | undefined;
  onOpen: (id: string) => void;
  onBack: () => void;
}) {
  const [expanded, setExpanded] = useState(false);
  if (selectedId !== undefined) {
    const position = data.positions.find((item) => item.id === selectedId);
    if (position === undefined) return <MissingRecord label="position" onBack={onBack} />;
    const identifiers = position.identifiers ?? [];
    const attributes = Object.entries(position.values ?? {});
    return (
      <DetailSurface eyebrow="Holding details" title={position.name} onBack={onBack}>
        <InvestingHelp
          terms={["holding", "exposure", "market value", "cost basis", "notional", "as of"]}
        />
        <dl className="detail-facts">
          <Fact label="Side" value={position.side} />
          <Fact label="Exposure type" value={position.exposureType} />
          <Fact label="Size" value={formatExposureSize(position)} />
          <Fact label="Currency" value={position.currency ?? "Not stated"} />
          <Fact label="Valuation basis" value={valuationBasis(position.values)} />
          <Fact
            label="Live market value"
            value={
              position.marketValuation === undefined
                ? "Unavailable"
                : formatMoney(
                    position.marketValuation.marketValue,
                    position.marketValuation.currency,
                  )
            }
          />
          <Fact
            label="Latest market price"
            value={
              position.marketValuation === undefined
                ? "Unavailable"
                : `${formatMoney(position.marketValuation.price, position.marketValuation.currency)} · ${position.marketValuation.provider}/${position.marketValuation.feed}`
            }
          />
          <Fact
            label="Market data as of"
            value={formatTimestamp(position.marketValuation?.priceAsOf)}
          />
          <Fact label="Pinned portfolio version" value={pinnedBookVersion(data.book)} />
        </dl>
        <section className="detail-section">
          <h2>Identifiers</h2>
          {identifiers.length === 0 ? (
            <p>No governed instrument identifiers are attached.</p>
          ) : (
            <dl className="identifier-list">
              {identifiers.map((identifier) => (
                <div key={`${identifier.scheme}:${identifier.value}`}>
                  <dt>{identifier.scheme}</dt>
                  <dd>
                    {identifier.value}
                    {identifier.scope === undefined ? "" : ` · ${identifier.scope}`}
                  </dd>
                </div>
              ))}
            </dl>
          )}
        </section>
        <button
          className="show-more-button"
          type="button"
          aria-expanded={expanded}
          onClick={() => setExpanded((current) => !current)}
        >
          {expanded ? "Show less" : "Show more"}
        </button>
        {expanded ? (
          <>
            <section className="detail-section">
              <h2>Source and version</h2>
              <dl className="detail-facts compact">
                <Fact
                  label="Source record"
                  value={position.sourceRecordKey ?? "Current snapshot"}
                />
                <Fact label="Position revision" value={position.revision ?? "Current"} />
                <Fact label="Valid from" value={formatTimestamp(position.validFrom)} />
                <Fact label="Last updated" value={formatTimestamp(position.updatedAt)} />
              </dl>
            </section>
            <section className="detail-section">
              <h2>Valuation inputs</h2>
              {attributes.length === 0 ? (
                <p>No profile-specific valuation inputs are available.</p>
              ) : (
                <dl className="attribute-list">
                  {attributes.map(([key, value]) => (
                    <div key={key}>
                      <dt>{humanizeKey(key)}</dt>
                      <dd>{formatAttribute(value)}</dd>
                    </div>
                  ))}
                </dl>
              )}
            </section>
            <section className="detail-section">
              <h2>Analyst note</h2>
              <p>{position.note || "No analyst note is attached to this position."}</p>
            </section>
            <section className="detail-section">
              <h2>Tags</h2>
              <p>{position.tags.length === 0 ? "No tags" : position.tags.join(" · ")}</p>
            </section>
          </>
        ) : null}
      </DetailSurface>
    );
  }
  return (
    <CollectionSurface
      eyebrow="Your portfolio"
      title="Holdings"
      body="What you own, how much you hold, and the latest available values. Select a holding to see its details."
    >
      {data.positions.length === 0 ? (
        <EmptyCollection text="No holdings saved yet. Choose Add holdings to enter investments or import a file." />
      ) : (
        <div className="record-list">
          {data.positions.map((position) => (
            <button type="button" key={position.id} onClick={() => onOpen(position.id)}>
              <span>
                <strong>{position.name}</strong>
                <small>{position.note || position.exposureType}</small>
              </span>
              <span>
                {position.marketValuation === undefined
                  ? formatExposureSize(position)
                  : formatMoney(
                      position.marketValuation.marketValue,
                      position.marketValuation.currency,
                    )}
              </span>
            </button>
          ))}
        </div>
      )}
    </CollectionSurface>
  );
}

export function AssumptionsView({
  data,
  selectedId,
  role,
  busy,
  onOpen,
  onBack,
  onUpdate,
  onRetire,
}: {
  data: WorkspaceData;
  selectedId?: string | undefined;
  role: CurrentUser["activeMembership"]["role"];
  busy: boolean;
  onOpen: (id: string) => void;
  onBack: () => void;
  onUpdate: (
    id: string,
    input: {
      expectedRevision: number;
      text: string;
      confidence: "high" | "medium" | "low";
      confidenceRationale: string | null;
      falsificationText: string | null;
      reviewBy: string | null;
    },
  ) => Promise<boolean>;
  onRetire: (id: string, revision: number, rationale: string) => Promise<boolean>;
}) {
  const assumption =
    selectedId === undefined ? undefined : data.assumptions.find((item) => item.id === selectedId);
  const [expanded, setExpanded] = useState(false);
  const [editing, setEditing] = useState(false);
  const [retiring, setRetiring] = useState(false);
  const [statement, setStatement] = useState(assumption?.text ?? "");
  const [confidence, setConfidence] = useState<"high" | "medium" | "low">(
    assumption?.confidence ?? "medium",
  );
  const [rationale, setRationale] = useState(assumption?.confidenceRationale ?? "");
  const [falsification, setFalsification] = useState(assumption?.falsificationText ?? "");
  const [reviewBy, setReviewBy] = useState(toDateInput(assumption?.reviewBy));
  const [retirementReason, setRetirementReason] = useState("");

  useEffect(() => {
    setExpanded(false);
    setEditing(false);
    setRetiring(false);
    setStatement(assumption?.text ?? "");
    setConfidence(assumption?.confidence ?? "medium");
    setRationale(assumption?.confidenceRationale ?? "");
    setFalsification(assumption?.falsificationText ?? "");
    setReviewBy(toDateInput(assumption?.reviewBy));
    setRetirementReason("");
  }, [assumption?.id, assumption?.revision]);

  if (selectedId !== undefined) {
    if (assumption === undefined) return <MissingRecord label="assumption" onBack={onBack} />;
    const dependentScenarios = assumption.links.filter((link) => link.entityType === "scenario");
    const canWrite = role !== "viewer";

    const submitRevision = async (event: FormEvent) => {
      event.preventDefault();
      if (assumption.revision === undefined || statement.trim() === "") return;
      const saved = await onUpdate(assumption.id, {
        expectedRevision: assumption.revision,
        text: statement.trim(),
        confidence,
        confidenceRationale: rationale.trim() === "" ? null : rationale.trim(),
        falsificationText: falsification.trim() === "" ? null : falsification.trim(),
        reviewBy: reviewBy === "" ? null : new Date(`${reviewBy}T12:00:00.000Z`).toISOString(),
      });
      if (saved) setEditing(false);
    };

    const submitRetirement = async (event: FormEvent) => {
      event.preventDefault();
      if (assumption.revision === undefined || retirementReason.trim() === "") return;
      await onRetire(assumption.id, assumption.revision, retirementReason.trim());
    };

    return (
      <DetailSurface eyebrow="Declared belief" title={assumption.text} onBack={onBack}>
        <dl className="detail-facts">
          <Fact label="Confidence" value={assumption.confidence} />
          <Fact label="Revision" value={assumption.revision ?? "Current"} />
          <Fact
            label="Affirmation freshness"
            value={`${assumption.freshnessState} · ${assumption.affirmationAgeDays} day${assumption.affirmationAgeDays === 1 ? "" : "s"}`}
          />
          <Fact label="Tripwire status" value={assumption.tripwireStatus.label} />
          <Fact label="Review by" value={formatTimestamp(assumption.reviewBy, "Not scheduled")} />
          <Fact label="Pinned portfolio version" value={pinnedBookVersion(data.book)} />
        </dl>
        <section className="detail-section">
          <h2>Confidence rationale</h2>
          <p>{assumption.confidenceRationale ?? "No rationale has been published."}</p>
        </section>
        <section className="detail-section">
          <h2>Falsification condition</h2>
          <p>{assumption.falsificationText ?? "No falsification condition has been adopted."}</p>
        </section>
        <section className="detail-section">
          <h2>Dependent what-ifs</h2>
          {dependentScenarios.length === 0 ? (
            <p>No what-if directly stresses, relies on, or tests this assumption.</p>
          ) : (
            <ul className="dependant-list">
              {dependentScenarios.map((link) => (
                <li key={link.id}>
                  <strong>{linkedEntityLabel(data, link.entityType, link.entityId)}</strong>
                  <span>{humanizeKey(link.relation)}</span>
                </li>
              ))}
            </ul>
          )}
        </section>
        <button
          className="show-more-button"
          type="button"
          aria-expanded={expanded}
          onClick={() => setExpanded((current) => !current)}
        >
          {expanded ? "Show less" : "Show more"}
        </button>
        {expanded ? (
          <section className="detail-section">
            <h2>Governance history</h2>
            <dl className="detail-facts compact">
              <Fact label="Owner" value={assumption.ownerUserId ?? "Unassigned"} />
              <Fact label="Valid from" value={formatTimestamp(assumption.validFrom)} />
              <Fact label="Last updated" value={formatTimestamp(assumption.updatedAt)} />
              <Fact label="All linked records" value={assumption.links.length} />
            </dl>
            {assumption.links.length === 0 ? null : (
              <ul className="dependant-list all-links">
                {assumption.links.map((link) => (
                  <li key={link.id}>
                    <strong>{linkedEntityLabel(data, link.entityType, link.entityId)}</strong>
                    <span>
                      {humanizeKey(link.relation)} · {humanizeKey(link.entityType)}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </section>
        ) : null}
        {canWrite ? (
          <div className="detail-actions">
            <button
              className="button secondary"
              type="button"
              disabled={busy || assumption.revision === undefined}
              onClick={() => {
                setEditing((current) => !current);
                setRetiring(false);
              }}
            >
              {editing ? "Cancel revision" : "Revise assumption"}
            </button>
            <button
              className="button danger"
              type="button"
              disabled={busy || assumption.revision === undefined}
              onClick={() => {
                setRetiring((current) => !current);
                setEditing(false);
              }}
            >
              {retiring ? "Cancel retirement" : "Retire assumption"}
            </button>
          </div>
        ) : (
          <p className="readonly-note">Viewer access is read-only.</p>
        )}
        {editing ? (
          <form className="assumption-editor" onSubmit={(event) => void submitRevision(event)}>
            <p className="workflow-rule">
              Saving creates a new immutable portfolio version; it never overwrites this revision.
            </p>
            <label>
              Statement
              <textarea
                required
                maxLength={4000}
                value={statement}
                onChange={(event) => setStatement(event.currentTarget.value)}
              />
            </label>
            <label>
              Confidence
              <select
                value={confidence}
                onChange={(event) =>
                  setConfidence(event.currentTarget.value as "high" | "medium" | "low")
                }
              >
                <option value="high">High</option>
                <option value="medium">Medium</option>
                <option value="low">Low</option>
              </select>
            </label>
            <label>
              Review by
              <input
                type="date"
                value={reviewBy}
                onChange={(event) => setReviewBy(event.currentTarget.value)}
              />
            </label>
            <label>
              Confidence rationale
              <textarea
                maxLength={4000}
                value={rationale}
                onChange={(event) => setRationale(event.currentTarget.value)}
              />
            </label>
            <label>
              Falsification condition
              <textarea
                maxLength={4000}
                value={falsification}
                onChange={(event) => setFalsification(event.currentTarget.value)}
              />
            </label>
            <button className="button primary" disabled={busy || statement.trim() === ""}>
              {busy ? "Publishing…" : "Publish revision"}
            </button>
          </form>
        ) : null}
        {retiring ? (
          <form
            className="assumption-retirement"
            onSubmit={(event) => void submitRetirement(event)}
          >
            <p className="workflow-rule">
              Retirement creates a new portfolio version and queues every directly linked what-if
              for reassessment review.
            </p>
            <label>
              Retirement reason
              <textarea
                required
                maxLength={2000}
                value={retirementReason}
                onChange={(event) => setRetirementReason(event.currentTarget.value)}
              />
            </label>
            <button className="button danger" disabled={busy || retirementReason.trim() === ""}>
              {busy ? "Retiring…" : "Confirm retirement"}
            </button>
          </form>
        ) : null}
      </DetailSurface>
    );
  }
  return (
    <CollectionSurface
      eyebrow="Governed thesis inputs"
      title={data.book.terminology.assumption + "s"}
      body="Declared beliefs remain separate from generated what-if claims and retain their review state."
    >
      {data.assumptions.length === 0 ? (
        <EmptyCollection text="No active assumptions have been adopted." />
      ) : (
        <div className="record-list">
          {data.assumptions.map((assumption) => (
            <button type="button" key={assumption.id} onClick={() => onOpen(assumption.id)}>
              <span>
                <strong>{assumption.text}</strong>
                <small>{assumption.confidenceRationale ?? "No confidence rationale"}</small>
              </span>
              <span>{assumption.confidence}</span>
            </button>
          ))}
        </div>
      )}
    </CollectionSurface>
  );
}

export function ScenariosView({
  data,
  selectedId,
  client,
  role,
  busy,
  onOpen,
  onBack,
  onOpenPosition,
  onCreate,
}: {
  data: WorkspaceData;
  selectedId?: string | undefined;
  client: PolyfutureApiClient;
  role: CurrentUser["activeMembership"]["role"];
  busy: boolean;
  onOpen: (id: string) => void;
  onBack: () => void;
  onOpenPosition: (id: string) => void;
  onCreate: (input: ScenarioDraftInput) => void;
}) {
  if (selectedId !== undefined) {
    const scenario = data.scenarios.find((item) => item.id === selectedId);
    if (scenario === undefined) return <MissingRecord label="what-if" onBack={onBack} />;
    return (
      <ScenarioMechanismDetail
        scenario={scenario}
        client={client}
        onBack={onBack}
        onOpenPosition={onOpenPosition}
      />
    );
  }
  const canAnalyze = role !== "viewer";
  return (
    <CollectionSurface
      eyebrow="What-if library"
      title="What-if library"
      body="User-stated and engine-generated what-ifs share one governed lifecycle and immutable versions."
    >
      {canAnalyze ? (
        <ScenarioForm disabled={busy} onSubmit={onCreate} />
      ) : (
        <p className="permission-note">Viewer access is read-only.</p>
      )}
      {data.scenarios.length === 0 ? (
        <EmptyCollection text="No what-ifs have been stated or generated." />
      ) : (
        <div className="record-list">
          {data.scenarios.map((scenario) => (
            <button type="button" key={scenario.id} onClick={() => onOpen(scenario.id)}>
              <span>
                <strong>{scenario.currentVersion?.name ?? "Elaboration pending"}</strong>
                <small>{scenario.currentVersion?.thesis ?? scenario.origin}</small>
              </span>
              <span>{scenario.status.replaceAll("_", " ")}</span>
            </button>
          ))}
        </div>
      )}
    </CollectionSurface>
  );
}

export function ScenarioLibraryView({
  client,
  books,
  selectedId,
  role,
  onOpen,
  onBack,
  onOpenPosition,
  onCreate,
  onOpenCoverage,
  onOpenDiff,
}: {
  client: PolyfutureApiClient;
  books: readonly BookRecord[];
  selectedId?: string | undefined;
  role: CurrentUser["activeMembership"]["role"];
  onOpen: (id: string) => void;
  onBack: () => void;
  onOpenPosition: (bookId: string, positionId: string) => void;
  onCreate: () => void;
  onOpenCoverage: () => void;
  onOpenDiff: (scenarioId: string, fromVersionId: string, toVersionId: string) => void;
}) {
  const [scenarios, setScenarios] = useState<ScenarioCatalogRecord[]>();
  const [matrix, setMatrix] = useState<ScenarioExposureMatrix>();
  const [presentation, setPresentation] = useState<"map" | "list" | "matrix">("map");
  const [error, setError] = useState<string>();
  const [matrixError, setMatrixError] = useState<string>();
  const [scenarioAction, setScenarioAction] = useState<string>();
  const canAnalyze = role !== "viewer";

  useEffect(() => {
    let active = true;
    setError(undefined);
    void client
      .listScenarios()
      .then((records) => {
        if (active) setScenarios(records);
      })
      .catch((caught: unknown) => {
        if (active) {
          setError(caught instanceof Error ? caught.message : "Unable to load what-if library");
        }
      });
    return () => {
      active = false;
    };
  }, [client]);

  useEffect(() => {
    let active = true;
    setMatrixError(undefined);
    void client
      .getScenarioExposureMatrix()
      .then((projection) => {
        if (active) setMatrix(projection);
      })
      .catch((caught: unknown) => {
        if (active) {
          setMatrixError(
            caught instanceof Error ? caught.message : "Unable to load the exposure matrix",
          );
        }
      });
    return () => {
      active = false;
    };
  }, [client]);

  useEffect(() => {
    if (selectedId === undefined) return () => undefined;
    let active = true;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const refreshSelectedScenario = async () => {
      try {
        const updated = await client.getScenario(selectedId);
        if (!active) return;
        setScenarios((records) =>
          records?.map((record) => (record.id === updated.id ? { ...record, ...updated } : record)),
        );
        if (updated.status === "elaborating" || updated.status === "materializing") {
          timer = setTimeout(() => void refreshSelectedScenario(), 1_500);
        }
      } catch (caught) {
        if (active) {
          setError(caught instanceof Error ? caught.message : "Unable to refresh the what-if run");
        }
      }
    };
    void refreshSelectedScenario();
    return () => {
      active = false;
      if (timer !== undefined) clearTimeout(timer);
    };
  }, [client, selectedId]);

  if (error !== undefined) {
    return (
      <DetailSurface
        eyebrow="What-if library"
        title="The library could not be loaded"
        onBack={onBack}
      >
        <p role="alert">{error}</p>
      </DetailSurface>
    );
  }
  if (scenarios === undefined) {
    return (
      <CollectionSurface
        eyebrow="What-if library"
        title="Loading what-ifs…"
        body="Reading the tenant-scoped governed catalog."
      >
        <p role="status">Loading the authoritative what-if library.</p>
      </CollectionSurface>
    );
  }
  if (selectedId !== undefined) {
    const scenario = scenarios.find((record) => record.id === selectedId);
    if (scenario === undefined) return <MissingRecord label="what-if" onBack={onBack} />;
    return (
      <>
        <ScenarioMechanismDetail
          scenario={scenario}
          client={client}
          onBack={onBack}
          onOpenPosition={(positionId) => onOpenPosition(scenario.bookId, positionId)}
          onOpenDiff={(fromVersionId, toVersionId) =>
            onOpenDiff(scenario.id, fromVersionId, toVersionId)
          }
          {...(canAnalyze && scenario.status === "proposed"
            ? {
                startingElaboration: scenarioAction === scenario.id,
                onStartElaboration: async () => {
                  setScenarioAction(scenario.id);
                  try {
                    const updated = await client.acceptScenario(scenario.id);
                    setScenarios((records) =>
                      records?.map((record) =>
                        record.id === updated.id ? { ...record, ...updated } : record,
                      ),
                    );
                  } catch (caught) {
                    setError(
                      caught instanceof Error ? caught.message : "Unable to start elaboration",
                    );
                  } finally {
                    setScenarioAction(undefined);
                  }
                },
              }
            : {})}
        />
        {canAnalyze ? (
          <div className="collection-actions">
            <RecordOutcome
              subject="scenario"
              onRecord={async (input) => {
                await client.recordScenarioOutcome(scenario.id, {
                  materialized: input.happened,
                  ...(input.on === undefined ? {} : { materializedOn: input.on }),
                  notes: input.notes,
                });
              }}
            />
          </div>
        ) : null}
      </>
    );
  }

  const matrixRowsByScenario = new Map(
    matrix?.rows.map((row) => [row.scenarioId, row] as const) ?? [],
  );
  return (
    <CollectionSurface
      eyebrow={`What-ifs · ${scenarios.filter((scenario) => scenario.status === "active").length} monitored`}
      title="Explore how the future could branch."
      body="Every scenario connects plain-language assumptions to observable evidence and portfolio outcomes."
    >
      <div className="collection-actions">
        <button className="button secondary" type="button" onClick={onOpenCoverage}>
          Coverage gaps
        </button>
        {canAnalyze && books.length > 0 ? (
          <button className="button button-primary" type="button" onClick={onCreate}>
            + New what-if
          </button>
        ) : canAnalyze ? (
          <p>Create a portfolio before stating a what-if.</p>
        ) : (
          <p className="permission-note">Viewer access is read-only.</p>
        )}
      </div>
      {scenarios.length === 0 ? (
        <EmptyScenarioStudio canCreate={canAnalyze && books.length > 0} onCreate={onCreate} />
      ) : presentation === "map" ? (
        <ScenarioCatalogMap scenarios={scenarios} matrix={matrix} onOpen={onOpen} />
      ) : presentation === "list" ? (
        <div className="library-table-scroll">
          <table className="library-table">
            <thead>
              <tr>
                <th>What-if</th>
                <th>Lifecycle</th>
                <th>Origin</th>
                <th>Impact range</th>
                <th>Horizon</th>
                <th>Revised</th>
              </tr>
            </thead>
            <tbody>
              {scenarios.map((scenario) => {
                const row = matrixRowsByScenario.get(scenario.id);
                return (
                  <tr key={scenario.id}>
                    <td>
                      <button
                        className="table-link"
                        type="button"
                        onClick={() => onOpen(scenario.id)}
                      >
                        <strong>{scenario.currentVersion?.name ?? "Elaboration pending"}</strong>
                        <small>{scenario.bookName}</small>
                      </button>
                    </td>
                    <td>{scenario.status.replaceAll("_", " ")}</td>
                    <td>{scenario.origin.replaceAll("_", " ")}</td>
                    <td>
                      {row === undefined ? "Loading assessment…" : summarizeMatrixImpact(row.cells)}
                    </td>
                    <td>
                      {scenario.currentVersion?.horizonDays === undefined
                        ? "Not declared"
                        : `${scenario.currentVersion.horizonDays} days`}
                    </td>
                    <td>{scenario.updatedAt.slice(0, 10)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : matrixError !== undefined ? (
        <div className="error-banner" role="alert">
          <p>The live matrix could not be loaded: {matrixError}</p>
          <button
            className="button secondary"
            type="button"
            onClick={() => setPresentation("list")}
          >
            Return to list
          </button>
        </div>
      ) : matrix === undefined ? (
        <p role="status">Building the live what-if × exposure matrix…</p>
      ) : (
        <>
          <aside className="matrix-coverage-summary" aria-label="Matrix coverage">
            <strong>
              {matrix.coverage.coveredExposures} of {matrix.coverage.totalExposures} current
              exposures have a modelled path
            </strong>
            <span>{matrix.coverage.explanation}</span>
            <button className="button secondary" type="button" onClick={onOpenCoverage}>
              Open coverage grid
            </button>
          </aside>
          <div className="library-table-scroll">
            <table className="library-table scenario-exposure-matrix">
              <caption>What-if × exposure · assessed impact range</caption>
              <thead>
                <tr>
                  <th>What-if</th>
                  {matrix.exposures.map((exposure) => (
                    <th key={exposure.positionId}>
                      {exposure.positionName}
                      <small>
                        {exposure.bookName} · {exposure.side} {formatExposureSize(exposure)}
                      </small>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {matrix.rows.map((row) => (
                  <tr key={row.scenarioId}>
                    <th scope="row">
                      <button
                        className="table-link"
                        type="button"
                        onClick={() => onOpen(row.scenarioId)}
                      >
                        {row.name}
                        <small>{row.bookName}</small>
                      </button>
                    </th>
                    {matrix.exposures.map((exposure) => {
                      const impact = row.cells.find(
                        (cell) => cell.positionId === exposure.positionId,
                      );
                      return (
                        <td
                          key={exposure.positionId}
                          className={impact === undefined ? "matrix-gap" : "matrix-impact"}
                        >
                          {impact === undefined ? (
                            <span>no path</span>
                          ) : (
                            <button
                              type="button"
                              onClick={() => onOpenPosition(exposure.bookId, exposure.positionId)}
                              aria-label={`${row.name} impact on ${exposure.positionName}: ${formatImpactRange(impact)}`}
                            >
                              {formatImpactRange(impact)}
                            </button>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="matrix-legend">
            Empty cells mean no modelled transmission path, not zero impact. Ranges are assessments;
            probability is not calibrated.
          </p>
        </>
      )}
      <details className="pf-view-controls pf-alternative-views">
        <summary>Alternative views</summary>
        <div className="segmented-control" role="group" aria-label="Library presentation">
          {(["map", "list", "matrix"] as const).map((mode) => (
            <button
              type="button"
              key={mode}
              className={presentation === mode ? "selected" : ""}
              aria-pressed={presentation === mode}
              onClick={() => setPresentation(mode)}
            >
              {mode === "map" ? "Butterfly map" : mode === "list" ? "List" : "Matrix"}
            </button>
          ))}
        </div>
      </details>
    </CollectionSurface>
  );
}

function EmptyScenarioStudio({
  canCreate,
  onCreate,
}: {
  canCreate: boolean;
  onCreate: () => void;
}) {
  return (
    <div className="pf-scenario-library-map pf-scenario-empty-studio">
      <aside className="pf-scenario-selector" aria-label="What-if scenarios">
        <article className="pf-empty-scenario-card">
          <span className="pf-kicker">Your first branch</span>
          <strong>State a change in plain language</strong>
          <small>
            Polyfuture will connect it to evidence, assumptions, mechanisms, and outcomes.
          </small>
          {canCreate ? (
            <button className="button button-primary" type="button" onClick={onCreate}>
              State a what-if
            </button>
          ) : null}
        </article>
      </aside>
      <section
        className="pf-library-butterfly pf-library-butterfly-empty"
        aria-label="Empty scenario map"
      >
        <header>
          <div>
            <p className="pf-kicker">Butterfly-effect map</p>
            <h2>No scenario is active yet</h2>
          </div>
          <small>
            Nothing here is simulated or monitored until you create and approve a what-if.
          </small>
        </header>
        <div className="pf-library-flow" aria-hidden="true">
          {[
            ["Event", "What changes?"],
            ["Evidence", "What would show it?"],
            ["Assumption", "What belief changes?"],
            ["Mechanism", "How does it propagate?"],
            ["Outcome", "What could it affect?"],
          ].map(([label, prompt], index) => (
            <Fragment key={label}>
              <article className="pf-flow-node pf-flow-placeholder">
                <span>{label}</span>
                <strong>{prompt}</strong>
              </article>
              {index === 4 ? null : <span>→</span>}
            </Fragment>
          ))}
        </div>
        <footer>
          <div>
            <span>Evidence</span>
            <strong>Not connected</strong>
          </div>
          <div>
            <span>Monitoring</span>
            <strong>Not started</strong>
          </div>
          <div>
            <span>Portfolio effect</span>
            <strong>Not assessed</strong>
          </div>
        </footer>
      </section>
    </div>
  );
}

function ScenarioCatalogMap({
  scenarios,
  matrix,
  onOpen,
}: {
  scenarios: readonly ScenarioCatalogRecord[];
  matrix: ScenarioExposureMatrix | undefined;
  onOpen: (id: string) => void;
}) {
  const [activeId, setActiveId] = useState(scenarios[0]?.id ?? "");
  const active = scenarios.find((scenario) => scenario.id === activeId) ?? scenarios[0];
  if (active === undefined) return null;
  const version = active.currentVersion;
  const matrixRow = matrix?.rows.find((row) => row.scenarioId === active.id);
  const impact =
    matrixRow === undefined ? "Assessment pending" : summarizeMatrixImpact(matrixRow.cells);
  const probability =
    active.probabilityPctUncalibrated === undefined
      ? "Not calibrated"
      : `${active.probabilityPctUncalibrated}% uncalibrated`;

  return (
    <div className="pf-scenario-library-map">
      <aside className="pf-scenario-selector" aria-label="What-if scenarios">
        {scenarios.map((scenario) => (
          <button
            type="button"
            key={scenario.id}
            className={scenario.id === active.id ? "selected" : ""}
            aria-pressed={scenario.id === active.id}
            onClick={() => setActiveId(scenario.id)}
          >
            <strong>{scenario.currentVersion?.name ?? "Elaboration pending"}</strong>
            <small>
              {scenario.status.replaceAll("_", " ")} · {scenario.bookName}
            </small>
          </button>
        ))}
      </aside>
      <section className="pf-library-butterfly" aria-label={`${version?.name ?? "What-if"} map`}>
        <header>
          <div>
            <p className="pf-kicker">Active what-if</p>
            <h2>{version?.name ?? "Elaboration pending"}</h2>
          </div>
          <button className="button" type="button" onClick={() => onOpen(active.id)}>
            Open full scenario
          </button>
        </header>
        <div className="pf-library-flow">
          <article className="pf-flow-node pf-tone-warning">
            <span>Event</span>
            <strong>{version?.thesis ?? "The initiating change has not been elaborated."}</strong>
          </article>
          <span aria-hidden="true">→</span>
          <article className="pf-flow-node">
            <span>Evidence</span>
            <strong>Observable sources and tripwires</strong>
            <small>Open the scenario to inspect live evidence.</small>
          </article>
          <span aria-hidden="true">→</span>
          <article className="pf-flow-node pf-tone-negative">
            <span>Assumption</span>
            <strong>{version?.falsificationText ?? "Falsifier not yet stated"}</strong>
          </article>
          <span aria-hidden="true">→</span>
          <article className="pf-flow-node">
            <span>Mechanism</span>
            <strong>{active.status.replaceAll("_", " ")}</strong>
            <small>{active.origin.replaceAll("_", " ")} origin</small>
          </article>
          <span aria-hidden="true">→</span>
          <article className="pf-flow-node pf-tone-positive">
            <span>Outcome</span>
            <strong>{version?.pnlEstimate ?? impact}</strong>
          </article>
        </div>
        <footer>
          <div>
            <span>Probability</span>
            <strong>{probability}</strong>
          </div>
          <div>
            <span>Horizon</span>
            <strong>
              {version?.horizonDays === undefined ? "Not declared" : `${version.horizonDays} days`}
            </strong>
          </div>
          <div>
            <span>Portfolio effect</span>
            <strong>{impact}</strong>
          </div>
        </footer>
      </section>
    </div>
  );
}

export function ScenarioMechanismDetail({
  scenario,
  client,
  onBack,
  onOpenPosition,
  onOpenDiff,
  onStartElaboration,
  startingElaboration = false,
}: {
  scenario: ScenarioRecord;
  client: PolyfutureApiClient;
  onBack: () => void;
  onOpenPosition: (id: string) => void;
  onOpenDiff?: (fromVersionId: string, toVersionId: string) => void;
  onStartElaboration?: () => Promise<void>;
  startingElaboration?: boolean;
}) {
  const [mode, setMode] = useState<"path" | "graph">("graph");
  const [zoom, setZoom] = useState<70 | 85 | 100 | 125 | 150>(100);
  const [fullscreen, setFullscreen] = useState(false);
  const mechanismRef = useRef<HTMLDivElement>(null);
  const [projection, setProjection] = useState<MechanismProjection>();
  const [selectedNodeKey, setSelectedNodeKey] = useState<string>();
  const [versions, setVersions] = useState<ScenarioVersionSummary[]>([]);
  const [compareVersionId, setCompareVersionId] = useState("");
  const [versionDiff, setVersionDiff] = useState<MechanismVersionDiff>();
  const [studioLens, setStudioLens] = useState<"all" | "supports" | "contradicts">("all");
  const [error, setError] = useState<string>();
  const versionId = scenario.currentVersion?.id;
  const draftVersion = scenario.candidateVersion;

  useEffect(() => {
    let active = true;
    setProjection(undefined);
    setError(undefined);
    if (versionId === undefined) return () => undefined;
    const load =
      mode === "path" ? client.getScenarioPath(versionId) : client.getScenarioGraph(versionId);
    void load
      .then((value) => {
        if (active) setProjection(value);
      })
      .catch((caught: unknown) => {
        if (active) setError(caught instanceof Error ? caught.message : "Unable to load mechanism");
      });
    return () => {
      active = false;
    };
  }, [client, mode, versionId]);

  useEffect(() => {
    setSelectedNodeKey(undefined);
  }, [versionId]);

  useEffect(() => {
    const changed = () => setFullscreen(document.fullscreenElement === mechanismRef.current);
    document.addEventListener("fullscreenchange", changed);
    return () => document.removeEventListener("fullscreenchange", changed);
  }, []);

  useEffect(() => {
    let active = true;
    void client
      .listScenarioVersions(scenario.id)
      .then((items) => {
        if (active) setVersions(items);
      })
      .catch(() => {
        if (active) setVersions([]);
      });
    return () => {
      active = false;
    };
  }, [client, scenario.id]);

  useEffect(() => {
    let active = true;
    setVersionDiff(undefined);
    if (versionId === undefined || compareVersionId === "") return () => undefined;
    void client
      .getScenarioVersionDiff(compareVersionId, versionId)
      .then((value) => {
        if (active) setVersionDiff(value);
      })
      .catch((caught: unknown) => {
        if (active)
          setError(caught instanceof Error ? caught.message : "Unable to compare versions");
      });
    return () => {
      active = false;
    };
  }, [client, compareVersionId, versionId]);

  if (scenario.status === "proposed" && draftVersion !== undefined) {
    return (
      <DetailSurface
        eyebrow="Proposed what-if · governed draft"
        title={draftVersion.name}
        onBack={onBack}
      >
        <aside className="draft-boundary scenario-draft-action">
          <div>
            <strong>Saved draft · no run started</strong>
            <p>
              This is a separate governed record. Its wording and pinned portfolio version are
              durable, but model work and monitoring remain off until an analyst submits it.
            </p>
          </div>
          {onStartElaboration === undefined ? null : (
            <button
              className="button button-primary"
              type="button"
              disabled={startingElaboration}
              onClick={() => void onStartElaboration()}
            >
              {startingElaboration ? "Queueing…" : "Submit for elaboration"}
            </button>
          )}
        </aside>
        <dl className="detail-facts compact">
          <Fact label="Lifecycle" value="Proposed · not monitored" />
          <Fact label="Polarity" value={scenario.polarity} />
          <Fact
            label="Pinned portfolio version"
            value={draftVersion.bookVersionId ?? "Recorded by server"}
          />
          <Fact label="Draft version" value={draftVersion.versionNo ?? 1} />
        </dl>
        <section className="detail-section">
          <h2>Exact stated what-if</h2>
          <p>{draftVersion.thesis}</p>
        </section>
        <section className="detail-section">
          <h2>Required falsifier</h2>
          <p>{draftVersion.falsificationText ?? "Not recorded"}</p>
        </section>
        {draftVersion.notes === undefined ? null : (
          <section className="detail-section">
            <h2>Provenance note</h2>
            <p>{draftVersion.notes}</p>
          </section>
        )}
      </DetailSurface>
    );
  }
  if (scenario.status === "elaborating" && draftVersion !== undefined) {
    return (
      <DetailSurface
        eyebrow="Governed draft · targeted analysis running"
        title={draftVersion.name}
        onBack={onBack}
      >
        <aside className="draft-boundary scenario-draft-action" role="status">
          <div>
            <strong>Scenario created · analysis in progress</strong>
            <p>
              Polyfuture is building the causal path and assessing the pinned portfolio. This page
              refreshes automatically. Publication and monitoring remain off.
            </p>
          </div>
        </aside>
        <dl className="detail-facts compact">
          <Fact label="Lifecycle" value="Elaborating · not monitored" />
          <Fact label="Polarity" value={scenario.polarity} />
          <Fact
            label="Pinned portfolio version"
            value={draftVersion.bookVersionId ?? "Recorded by server"}
          />
          <Fact label="Draft version" value={draftVersion.versionNo ?? 1} />
        </dl>
        <section className="detail-section">
          <h2>Exact stated what-if</h2>
          <p>{draftVersion.thesis}</p>
        </section>
        <section className="detail-section">
          <h2>Required falsifier</h2>
          <p>{draftVersion.falsificationText ?? "Not recorded"}</p>
        </section>
        {draftVersion.notes === undefined ? null : (
          <section className="detail-section">
            <h2>Agent draft parameters</h2>
            <p className="pre-wrap">{draftVersion.notes}</p>
          </section>
        )}
      </DetailSurface>
    );
  }
  if (versionId === undefined) {
    return (
      <DetailSurface eyebrow="What-if" title="Elaboration pending" onBack={onBack}>
        <p>No governed what-if version is available yet.</p>
      </DetailSurface>
    );
  }
  if (error !== undefined) {
    return (
      <DetailSurface eyebrow="What-if mechanism" title="Unable to load mechanism" onBack={onBack}>
        <p role="alert">{error}</p>
      </DetailSurface>
    );
  }
  if (projection === undefined) {
    return (
      <DetailSurface eyebrow="What-if mechanism" title="Loading mechanism…" onBack={onBack}>
        <p role="status">Loading the pinned what-if version.</p>
      </DetailSurface>
    );
  }
  const selectedNode = projection.nodes.find((node) => node.key === selectedNodeKey);
  const impactRange = summarizeImpactRange(projection);
  return (
    <ProductPage className="pf-scenario-studio">
      <ProductHeading
        eyebrow={`${scenario.origin.replaceAll("_", " ")} what-if · version ${projection.basis.versionNo}`}
        title={projection.name}
        body={<p>{projection.thesis}</p>}
        actions={
          <>
            <button className="button button-secondary" type="button" onClick={onBack}>
              Back to what-ifs
            </button>
            <StatusDot
              label={scenario.status.replaceAll("_", " ")}
              tone={scenario.status === "materialized" ? "live" : "warning"}
            />
          </>
        }
      />
      {scenario.status === "proposed" ? (
        <aside className="draft-boundary scenario-draft-action">
          <div>
            <strong>Saved draft · no run started</strong>
            <p>
              This wording and its pinned portfolio version are durable, but no model work or
              monitoring will begin until an analyst submits it.
            </p>
          </div>
          {onStartElaboration === undefined ? null : (
            <button
              className="button button-primary"
              type="button"
              disabled={startingElaboration}
              onClick={() => void onStartElaboration()}
            >
              {startingElaboration ? "Queueing…" : "Submit for elaboration"}
            </button>
          )}
        </aside>
      ) : null}
      <aside className="mechanism-impact-summary" aria-label="Assessed impact">
        <span>Assessed impact on exposure</span>
        <strong>{impactRange}</strong>
        <small>
          {scenario.currentVersion?.horizonDays === undefined
            ? "Horizon not declared"
            : `${scenario.currentVersion.horizonDays}-day horizon`}{" "}
          · probability not calibrated
        </small>
      </aside>
      <section className="pf-studio-controls" aria-label="Scenario view controls">
        <div>
          <p className="pf-kicker">Active evidence lens</p>
          <div className="segmented-control" role="group" aria-label="Evidence lens">
            {(["all", "supports", "contradicts"] as const).map((lens) => (
              <button
                type="button"
                key={lens}
                className={studioLens === lens ? "selected" : ""}
                aria-pressed={studioLens === lens}
                onClick={() => setStudioLens(lens)}
              >
                {lens === "all"
                  ? "Current facts"
                  : lens === "supports"
                    ? "Supporting evidence"
                    : "Contradicting evidence"}
              </button>
            ))}
          </div>
        </div>
        <p>
          Changing the lens highlights recorded evidence. It does not invent or recalibrate
          probability.
        </p>
      </section>

      <ButterflyEffectStudio
        projection={projection}
        selectedNodeKey={selectedNodeKey}
        onSelectNode={setSelectedNodeKey}
        lens={studioLens}
      />

      <div className="pf-scenario-context-grid">
        <ProductPanel
          eyebrow="Load-bearing beliefs"
          title="Assumptions and conditions"
          meta={`${projection.nodes.filter((node) => kernelColumnForNode(node.type) === "conditions").length} connected`}
        >
          <div className="pf-context-list">
            {projection.nodes
              .filter((node) => kernelColumnForNode(node.type) === "conditions")
              .map((node) => (
                <button type="button" key={node.key} onClick={() => setSelectedNodeKey(node.key)}>
                  <strong>{node.label}</strong>
                  <small>{node.type.replaceAll("_", " ")}</small>
                </button>
              ))}
            {projection.nodes.every((node) => kernelColumnForNode(node.type) !== "conditions") ? (
              <p>No explicit condition nodes are present in this version.</p>
            ) : null}
          </div>
        </ProductPanel>
        <ProductPanel
          eyebrow="Observable signals"
          title="Evidence and tripwires"
          meta={`${projection.tripwires.length} tripwires`}
        >
          <div className="pf-context-list">
            {projection.tripwires.slice(0, 5).map((tripwire) => (
              <article key={tripwire.id}>
                <strong>{tripwire.signal}</strong>
                <small>
                  {tripwire.thresholdText} · {tripwire.evidenceStatus.replaceAll("_", " ")}
                </small>
              </article>
            ))}
            {projection.tripwires.length === 0 ? (
              <p>No tripwires are attached to this version.</p>
            ) : null}
          </div>
        </ProductPanel>
      </div>

      <ProgressiveDisclosure
        label="Advanced mechanism, provenance, version comparison, and evidence controls"
        summary="Full governed detail"
        open={selectedNodeKey !== undefined}
      >
        <div className="mechanism-toolbar">
          <div className="segmented-control" role="group" aria-label="Mechanism presentation">
            {(["path", "graph"] as const).map((item) => (
              <button
                type="button"
                key={item}
                aria-pressed={mode === item}
                className={mode === item ? "selected" : ""}
                onClick={() => setMode(item)}
              >
                {item === "path" ? "Path" : "Graph"}
              </button>
            ))}
          </div>
          <div className="mechanism-zoom" role="group" aria-label="Mechanism zoom">
            {[70, 85, 100, 125, 150].map((value) => (
              <button
                type="button"
                key={value}
                className={zoom === value ? "selected" : ""}
                aria-pressed={zoom === value}
                onClick={() => setZoom(value as 70 | 85 | 100 | 125 | 150)}
              >
                {value}%
              </button>
            ))}
            <button type="button" onClick={() => setZoom(100)}>
              Reset
            </button>
          </div>
          <ProvenanceDrawer
            title="What-if provenance"
            pins={[
              { label: "What-if version", value: projection.basis.scenarioVersionId },
              {
                label: "Portfolio version",
                value: projection.basis.bookVersionId,
                ...(projection.bookDrift.currentBookVersionId === null
                  ? {}
                  : { current: projection.bookDrift.currentBookVersionId }),
              },
              { label: "Ontology", value: projection.basis.ontologyVersionId },
              {
                label: "Agent manifest",
                value: projection.basis.agentManifestId ?? "Not model-produced",
              },
              ...(projection.basis.createdFromRunId === null
                ? []
                : [{ label: "Run", value: projection.basis.createdFromRunId }]),
            ]}
            {...(projection.bookDrift.drifted
              ? { movedExplanation: projection.bookDrift.explanation }
              : {})}
          />
        </div>

        {projection.bookDrift.drifted ? (
          <p className="drift-warning" role="status">
            Portfolio drift: {projection.bookDrift.explanation}
          </p>
        ) : null}

        <section className="detail-section">
          <h2>Thesis</h2>
          <p>{projection.thesis}</p>
        </section>
        <section className="detail-section falsification-panel">
          <h2>What would falsify this?</h2>
          <p>
            {projection.falsification.text ??
              "Missing. A falsification statement is required before a candidate can be promoted."}
          </p>
        </section>

        {versions.filter((version) => version.id !== versionId).length === 0 ? null : (
          <section
            className="detail-section version-comparison"
            aria-labelledby="version-diff-heading"
          >
            <div className="panel-heading">
              <div>
                <p className="section-kicker">Immutable history</p>
                <h2 id="version-diff-heading">Version comparison</h2>
              </div>
              <label>
                <span>Compare previous version</span>
                <select
                  value={compareVersionId}
                  onChange={(event) => setCompareVersionId(event.target.value)}
                >
                  <option value="">Choose version</option>
                  {versions
                    .filter((version) => version.id !== versionId)
                    .map((version) => (
                      <option value={version.id} key={version.id}>
                        Version {version.versionNo ?? "history"} · {version.state}
                      </option>
                    ))}
                </select>
              </label>
            </div>
            {compareVersionId !== "" && versionDiff === undefined ? (
              <p role="status">Computing deterministic version diff…</p>
            ) : versionDiff === undefined ? (
              <p>Select a prior version to inspect exact causal and exposure changes.</p>
            ) : (
              <>
                <VersionDiffSummary diff={versionDiff} />
                {onOpenDiff === undefined ? null : (
                  <button
                    className="button secondary"
                    type="button"
                    onClick={() => onOpenDiff(compareVersionId, versionId)}
                  >
                    Open full comparison
                  </button>
                )}
              </>
            )}
          </section>
        )}

        <div className="mechanism-layout" ref={mechanismRef}>
          <section className="detail-section mechanism-canvas" aria-labelledby="mechanism-heading">
            <div className="panel-heading">
              <h2 id="mechanism-heading">
                {mode === "path" ? "Ordered causal path" : "Causal graph"}
              </h2>
              <button
                className="button secondary"
                type="button"
                onClick={() => {
                  if (document.fullscreenElement === mechanismRef.current) {
                    void document.exitFullscreen();
                  } else {
                    void mechanismRef.current?.requestFullscreen();
                  }
                }}
              >
                {fullscreen ? "Exit full page" : "Full page"}
              </button>
            </div>
            <div
              className="mechanism-visual"
              style={{ "--mechanism-zoom": zoom / 100 } as CSSProperties}
            >
              {projection.nodes.length === 0 ? (
                <p>No causal nodes are present in this version.</p>
              ) : mode === "path" ? (
                <ol className="causal-path">
                  {projection.nodes.map((node) => (
                    <li key={node.key}>
                      <button
                        type="button"
                        aria-pressed={selectedNodeKey === node.key}
                        className={selectedNodeKey === node.key ? "selected" : ""}
                        onClick={() => setSelectedNodeKey(node.key)}
                      >
                        <small>{node.type.replaceAll("_", " ")}</small>
                        <strong>{node.label}</strong>
                      </button>
                      <PathStepEvidence
                        nodeKey={node.key}
                        supports={projection.evidenceRails.supports}
                        contradicts={projection.evidenceRails.contradicts}
                      />
                    </li>
                  ))}
                </ol>
              ) : (
                <CausalGraphCanvas
                  nodes={projection.nodes}
                  edges={projection.edges}
                  selectedNodeKey={selectedNodeKey}
                  onSelectNode={setSelectedNodeKey}
                />
              )}
            </div>
            <details className="textual-equivalent">
              <summary>Accessible textual equivalent</summary>
              <ol>
                {projection.nodes.map((node) => (
                  <li key={node.key}>
                    <strong>{node.label}</strong> ({node.type.replaceAll("_", " ")})
                    <ul>
                      {projection.edges
                        .filter((edge) => edge.from === node.key)
                        .map((edge) => (
                          <li key={`${edge.from}-${edge.predicate}-${edge.to}`}>
                            {edge.predicate.replaceAll("_", " ")} → {edge.to}
                          </li>
                        ))}
                    </ul>
                  </li>
                ))}
              </ol>
            </details>
          </section>
          <aside className="node-inspector" aria-live="polite">
            <h2>Node inspector</h2>
            {selectedNode === undefined ? (
              <p>Select a node to inspect its governed identity and links.</p>
            ) : (
              <dl className="detail-facts compact">
                <Fact label="Label" value={selectedNode.label} />
                <Fact label="Type" value={selectedNode.type.replaceAll("_", " ")} />
                <Fact label="Concept" value={selectedNode.conceptKey ?? "Not linked"} />
                <Fact label="Entity" value={selectedNode.entityId ?? "Not linked"} />
              </dl>
            )}
            {selectedNode === undefined ? null : (
              <>
                <NodeRelations
                  nodeKey={selectedNode.key}
                  nodes={projection.nodes}
                  edges={projection.edges}
                />
                <PathStepEvidence
                  nodeKey={selectedNode.key}
                  supports={projection.evidenceRails.supports}
                  contradicts={projection.evidenceRails.contradicts}
                />
              </>
            )}
          </aside>
        </div>

        <section className="detail-section">
          <h2>Affected exposures</h2>
          {projection.impacts.length === 0 ? (
            <p>No linked impacts are present.</p>
          ) : (
            <div className="record-list">
              {projection.impacts.map((impact) => (
                <button
                  type="button"
                  key={impact.positionId}
                  onClick={() => onOpenPosition(impact.positionId)}
                >
                  <span>
                    <strong>{impact.positionName}</strong>
                    <small>{impact.direction}</small>
                  </span>
                  <span>{impact.magnitude}</span>
                </button>
              ))}
            </div>
          )}
        </section>

        <div className="evidence-rails">
          <EvidenceRail
            title="Supports"
            status={projection.basis.evidenceStatus}
            links={projection.evidenceRails.supports}
          />
          <EvidenceRail
            title="Contradicts"
            status={projection.basis.evidenceStatus}
            links={projection.evidenceRails.contradicts}
          />
        </div>
        <p className="evidence-basis">{projection.basis.evidenceExplanation}</p>
        <dl className="detail-facts compact evidence-activation" aria-label="Evidence activation">
          <Fact
            label="Activation"
            value={`${Math.round(projection.basis.evidenceActivation.activation * 100)}%`}
          />
          <Fact
            label="Coverage"
            value={`${Math.round(projection.basis.evidenceActivation.evidenceCoverage * 100)}%`}
          />
          <Fact
            label="Corroboration"
            value={projection.basis.evidenceActivation.corroborationState.replaceAll("_", " ")}
          />
          <Fact
            label="Independent groups"
            value={`${projection.basis.evidenceActivation.activeIndependentGroups} active / ${projection.basis.evidenceActivation.eligibleIndependentGroups} eligible`}
          />
        </dl>
      </ProgressiveDisclosure>
    </ProductPage>
  );
}

function ButterflyEffectStudio({
  projection,
  selectedNodeKey,
  onSelectNode,
  lens,
}: {
  readonly projection: MechanismProjection;
  readonly selectedNodeKey: string | undefined;
  readonly onSelectNode: (key: string) => void;
  readonly lens: "all" | "supports" | "contradicts";
}) {
  const drivers = projection.nodes.filter((node) => kernelColumnForNode(node.type) === "drivers");
  const conditions = projection.nodes.filter(
    (node) => kernelColumnForNode(node.type) === "conditions",
  );
  const mechanisms = projection.nodes.filter(
    (node) => kernelColumnForNode(node.type) === "transmission",
  );
  const evidence =
    lens === "supports"
      ? projection.evidenceRails.supports
      : lens === "contradicts"
        ? projection.evidenceRails.contradicts
        : [...projection.evidenceRails.contradicts, ...projection.evidenceRails.supports];
  const eventNodes = drivers.length > 0 ? drivers : projection.nodes.slice(0, 1);
  const mechanismNodes =
    mechanisms.length > 0
      ? mechanisms
      : projection.nodes
          .filter((node) => !eventNodes.includes(node) && !conditions.includes(node))
          .slice(0, 4);
  return (
    <section className="pf-butterfly-studio" aria-labelledby="butterfly-heading">
      <header>
        <div>
          <p className="pf-kicker">Interactive transmission map</p>
          <h2 id="butterfly-heading">See the butterfly effect.</h2>
        </div>
        <span>
          {projection.nodes.length} nodes · {projection.edges.length} relationships
        </span>
      </header>
      <div className="pf-butterfly-columns">
        <ButterflyColumn label="Event" hint="What changed">
          {eventNodes.map((node) => (
            <ButterflyNode
              key={node.key}
              label={node.label}
              meta={node.type}
              selected={selectedNodeKey === node.key}
              onClick={() => onSelectNode(node.key)}
            />
          ))}
        </ButterflyColumn>
        <ButterflyColumn label="Evidence" hint={lens === "all" ? "For and against" : lens}>
          {evidence.slice(0, 4).map((item) => (
            <ButterflyNode
              key={item.id}
              label={item.contractName}
              meta={`${item.polarity} · ${item.sourceHealth ?? "not observed"}`}
              tone={item.polarity === "contradicts" ? "negative" : "positive"}
              selected={selectedNodeKey === item.nodeKey}
              onClick={() => onSelectNode(item.nodeKey)}
            />
          ))}
          {evidence.length === 0 ? (
            <ButterflyNode label="No observed evidence" meta="Coverage is explicit" tone="muted" />
          ) : null}
        </ButterflyColumn>
        <ButterflyColumn label="Assumptions" hint="What must remain true">
          {conditions.slice(0, 4).map((node) => (
            <ButterflyNode
              key={node.key}
              label={node.label}
              meta={node.type}
              selected={selectedNodeKey === node.key}
              onClick={() => onSelectNode(node.key)}
            />
          ))}
          {conditions.length === 0 ? (
            <ButterflyNode
              label="No explicit condition node"
              meta="Inspect the thesis and falsifier"
              tone="warning"
            />
          ) : null}
        </ButterflyColumn>
        <ButterflyColumn label="Mechanisms" hint="How change propagates">
          {mechanismNodes.slice(0, 4).map((node) => (
            <ButterflyNode
              key={node.key}
              label={node.label}
              meta={node.type}
              selected={selectedNodeKey === node.key}
              onClick={() => onSelectNode(node.key)}
            />
          ))}
        </ButterflyColumn>
        <ButterflyColumn label="Outcomes" hint="Portfolio effects" last>
          {projection.impacts.slice(0, 4).map((impact) => (
            <ButterflyNode
              key={impact.positionId}
              label={impact.positionName}
              meta={`${impact.direction} · ${impact.magnitude}`}
              tone={
                impact.direction.toLowerCase().includes("down") ||
                impact.direction.toLowerCase().includes("negative")
                  ? "negative"
                  : "neutral"
              }
            />
          ))}
          {projection.impacts.length === 0 ? (
            <ButterflyNode
              label="No linked impact"
              meta="Not the same as zero impact"
              tone="muted"
            />
          ) : null}
        </ButterflyColumn>
      </div>
      <footer>
        <div>
          <span>Evidence activation</span>
          <strong>{Math.round(projection.basis.evidenceActivation.activation * 100)}%</strong>
        </div>
        <div>
          <span>Coverage</span>
          <strong>{Math.round(projection.basis.evidenceActivation.evidenceCoverage * 100)}%</strong>
        </div>
        <div>
          <span>Corroboration</span>
          <strong>
            {projection.basis.evidenceActivation.corroborationState.replaceAll("_", " ")}
          </strong>
        </div>
        <div>
          <span>Portfolio effect</span>
          <strong>{summarizeImpactRange(projection)}</strong>
        </div>
      </footer>
    </section>
  );
}

function ButterflyColumn({
  label,
  hint,
  children,
  last = false,
}: {
  readonly label: string;
  readonly hint: string;
  readonly children: ReactNode;
  readonly last?: boolean;
}) {
  return (
    <section className={`pf-butterfly-column${last ? " is-last" : ""}`}>
      <header>
        <strong>{label}</strong>
        <small>{hint}</small>
      </header>
      <div>{children}</div>
    </section>
  );
}

function ButterflyNode({
  label,
  meta,
  tone = "neutral",
  selected = false,
  onClick,
}: {
  readonly label: string;
  readonly meta: string;
  readonly tone?: "neutral" | "positive" | "negative" | "warning" | "muted";
  readonly selected?: boolean;
  readonly onClick?: () => void;
}) {
  const className = `pf-butterfly-node pf-tone-${tone}${selected ? " is-selected" : ""}`;
  return onClick === undefined ? (
    <article className={className}>
      <strong>{label}</strong>
      <small>{meta.replaceAll("_", " ")}</small>
    </article>
  ) : (
    <button type="button" className={className} aria-pressed={selected} onClick={onClick}>
      <strong>{label}</strong>
      <small>{meta.replaceAll("_", " ")}</small>
    </button>
  );
}

const GRAPH_COLUMN_WIDTH = 190;
const GRAPH_NODE_WIDTH = 154;
const GRAPH_NODE_HEIGHT = 78;
const GRAPH_HEADER_HEIGHT = 54;
const GRAPH_ROW_HEIGHT = 116;

const MECHANISM_KERNEL_COLUMNS = [
  { key: "drivers", label: "Drivers" },
  { key: "conditions", label: "Conditions" },
  { key: "transmission", label: "Transmission" },
  { key: "outcomes", label: "Outcomes" },
  { key: "decisions", label: "Decisions" },
  { key: "evidence", label: "Evidence" },
] as const;

type MechanismKernelColumn = (typeof MECHANISM_KERNEL_COLUMNS)[number]["key"];
type MechanismNodeRecord = MechanismProjection["nodes"][number];
type MechanismEdgeRecord = MechanismProjection["edges"][number];
type MechanismEvidenceRecord = MechanismProjection["evidenceRails"]["supports"][number];

interface GraphNodeLayout {
  readonly node: MechanismNodeRecord;
  readonly column: number;
  readonly row: number;
  readonly x: number;
  readonly y: number;
}

export function CausalGraphCanvas({
  nodes,
  edges,
  selectedNodeKey,
  onSelectNode,
}: {
  readonly nodes: readonly MechanismNodeRecord[];
  readonly edges: readonly MechanismEdgeRecord[];
  readonly selectedNodeKey: string | undefined;
  readonly onSelectNode: (nodeKey: string) => void;
}) {
  const markerPrefix = useId().replaceAll(":", "");
  const layout = layoutMechanismNodes(nodes);
  const byKey = new Map(layout.map((item) => [item.node.key, item]));
  const maxRows = Math.max(
    1,
    ...MECHANISM_KERNEL_COLUMNS.map(
      (_, column) => layout.filter((item) => item.column === column).length,
    ),
  );
  const canvasWidth = GRAPH_COLUMN_WIDTH * MECHANISM_KERNEL_COLUMNS.length;
  const canvasHeight = GRAPH_HEADER_HEIGHT + maxRows * GRAPH_ROW_HEIGHT + 20;

  return (
    <div className="causal-graph-frame" aria-label="Causal graph">
      <div className="causal-graph-legend" aria-label="Edge polarity">
        {(["amplifies", "dampens", "gates"] as const).map((polarity) => (
          <span className={`edge-legend edge-${polarity}`} key={polarity}>
            {polarity}
          </span>
        ))}
      </div>
      <div
        className="causal-graph-canvas"
        role="list"
        aria-label="Causal graph nodes arranged by kernel stage"
        style={{ width: canvasWidth, height: canvasHeight }}
      >
        {MECHANISM_KERNEL_COLUMNS.map((column, index) => (
          <div
            className="mechanism-kernel-column"
            key={column.key}
            style={{ left: index * GRAPH_COLUMN_WIDTH, width: GRAPH_COLUMN_WIDTH }}
            aria-hidden="true"
          >
            <span>{String(index + 1).padStart(2, "0")}</span>
            <strong>{column.label}</strong>
          </div>
        ))}
        <svg
          className="causal-graph-edges"
          viewBox={`0 0 ${canvasWidth} ${canvasHeight}`}
          aria-hidden="true"
        >
          <defs>
            {(["amplifies", "dampens", "gates"] as const).map((polarity) => (
              <marker
                id={`${markerPrefix}-${polarity}`}
                key={polarity}
                markerWidth="8"
                markerHeight="8"
                refX="7"
                refY="4"
                orient="auto"
                markerUnits="strokeWidth"
              >
                <path d="M0,0 L8,4 L0,8 Z" className={`edge-marker edge-${polarity}`} />
              </marker>
            ))}
          </defs>
          {edges.map((edge) => {
            const from = byKey.get(edge.from);
            const to = byKey.get(edge.to);
            if (from === undefined || to === undefined) return null;
            return (
              <path
                key={`${edge.from}-${edge.predicate}-${edge.to}`}
                d={graphEdgePath(from, to)}
                className={`causal-edge edge-${edge.polarity}`}
                markerEnd={`url(#${markerPrefix}-${edge.polarity})`}
              />
            );
          })}
        </svg>
        {layout.map(({ node, x, y }) => (
          <button
            type="button"
            role="listitem"
            key={node.key}
            aria-pressed={selectedNodeKey === node.key}
            className={`causal-graph-node node-${kernelColumnForNode(node.type)}${selectedNodeKey === node.key ? " selected" : ""}`}
            style={{ left: x, top: y, width: GRAPH_NODE_WIDTH, minHeight: GRAPH_NODE_HEIGHT }}
            onClick={() => onSelectNode(node.key)}
          >
            <small>{node.type.replaceAll("_", " ")}</small>
            <strong>{node.label}</strong>
          </button>
        ))}
      </div>
    </div>
  );
}

function layoutMechanismNodes(nodes: readonly MechanismNodeRecord[]): readonly GraphNodeLayout[] {
  const rows = new Map<number, number>();
  return [...nodes]
    .sort(
      (left, right) =>
        (left.primaryPathOrder ?? Number.MAX_SAFE_INTEGER) -
          (right.primaryPathOrder ?? Number.MAX_SAFE_INTEGER) ||
        left.label.localeCompare(right.label),
    )
    .map((node) => {
      const column = MECHANISM_KERNEL_COLUMNS.findIndex(
        ({ key }) => key === kernelColumnForNode(node.type),
      );
      const row = rows.get(column) ?? 0;
      rows.set(column, row + 1);
      return {
        node,
        column,
        row,
        x: column * GRAPH_COLUMN_WIDTH + (GRAPH_COLUMN_WIDTH - GRAPH_NODE_WIDTH) / 2,
        y: GRAPH_HEADER_HEIGHT + row * GRAPH_ROW_HEIGHT + 14,
      };
    });
}

function kernelColumnForNode(type: string): MechanismKernelColumn {
  const normalized = type.toLowerCase().replaceAll("-", "_");
  if (/event|driver|trigger|shock/.test(normalized)) return "drivers";
  if (/condition|precondition|assumption/.test(normalized)) return "conditions";
  if (/outcome|impact|effect|loss|gain/.test(normalized)) return "outcomes";
  if (/decision|action|response/.test(normalized)) return "decisions";
  if (/evidence|indicator|tripwire|observation|signal/.test(normalized)) return "evidence";
  return "transmission";
}

function graphEdgePath(from: GraphNodeLayout, to: GraphNodeLayout): string {
  const fromCenterX = from.x + GRAPH_NODE_WIDTH / 2;
  const toCenterX = to.x + GRAPH_NODE_WIDTH / 2;
  const fromCenterY = from.y + GRAPH_NODE_HEIGHT / 2;
  const toCenterY = to.y + GRAPH_NODE_HEIGHT / 2;
  if (from.column === to.column) {
    const bend = fromCenterX + 42;
    return `M ${fromCenterX} ${fromCenterY} C ${bend} ${fromCenterY}, ${bend} ${toCenterY}, ${toCenterX} ${toCenterY}`;
  }
  const forward = to.column > from.column;
  const fromX = forward ? from.x + GRAPH_NODE_WIDTH : from.x;
  const toX = forward ? to.x : to.x + GRAPH_NODE_WIDTH;
  const midpoint = (fromX + toX) / 2;
  return `M ${fromX} ${fromCenterY} C ${midpoint} ${fromCenterY}, ${midpoint} ${toCenterY}, ${toX} ${toCenterY}`;
}

function NodeRelations({
  nodeKey,
  nodes,
  edges,
}: {
  readonly nodeKey: string;
  readonly nodes: readonly MechanismNodeRecord[];
  readonly edges: readonly MechanismEdgeRecord[];
}) {
  const labels = new Map(nodes.map((node) => [node.key, node.label]));
  const related = edges.filter((edge) => edge.from === nodeKey || edge.to === nodeKey);
  return (
    <section className="node-relations" aria-label="Node relationships">
      <h3>Relationships</h3>
      {related.length === 0 ? (
        <p>No relationships in this projection.</p>
      ) : (
        <ul>
          {related.map((edge) => (
            <li key={`${edge.from}-${edge.predicate}-${edge.to}`}>
              <span className={`edge-relation edge-${edge.polarity}`}>{edge.polarity}</span>{" "}
              {edge.from === nodeKey ? labels.get(edge.to) : labels.get(edge.from)}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function PathStepEvidence({
  nodeKey,
  supports,
  contradicts,
}: {
  readonly nodeKey: string;
  readonly supports: readonly MechanismEvidenceRecord[];
  readonly contradicts: readonly MechanismEvidenceRecord[];
}) {
  const rails = [
    {
      key: "supports",
      label: "Supports",
      links: supports.filter((link) => link.nodeKey === nodeKey),
    },
    {
      key: "contradicts",
      label: "Contradicts",
      links: contradicts.filter((link) => link.nodeKey === nodeKey),
    },
  ] as const;
  return (
    <div className="path-step-evidence" aria-label="Evidence linked to this mechanism step">
      {rails.map((rail) => (
        <section key={rail.key} className={`path-step-rail rail-${rail.key}`}>
          <h4>{rail.label}</h4>
          {rail.links.length === 0 ? (
            <p>No linked evidence</p>
          ) : (
            <ul>
              {rail.links.map((link) => (
                <li key={link.id}>
                  {link.contractName}
                  <small>{(link.evaluationState ?? "not observed").replaceAll("_", " ")}</small>
                </li>
              ))}
            </ul>
          )}
        </section>
      ))}
    </div>
  );
}

export function ScenarioVersionDiffView({
  scenarioId,
  fromVersionId,
  toVersionId,
  client,
  onBack,
}: {
  scenarioId: string;
  fromVersionId: string;
  toVersionId: string;
  client: PolyfutureApiClient;
  onBack: () => void;
}) {
  const [comparison, setComparison] = useState<{
    scenario: ScenarioRecord;
    from: MechanismProjection;
    to: MechanismProjection;
    diff: MechanismVersionDiff;
  }>();
  const [error, setError] = useState<string>();

  useEffect(() => {
    let active = true;
    setComparison(undefined);
    setError(undefined);
    void Promise.all([
      client.getScenario(scenarioId),
      client.getScenarioPath(fromVersionId),
      client.getScenarioPath(toVersionId),
      client.getScenarioVersionDiff(fromVersionId, toVersionId),
    ])
      .then(([scenario, from, to, diff]) => {
        if (!active) return;
        if (
          scenario.id !== diff.scenarioId ||
          from.basis.scenarioId !== scenario.id ||
          to.basis.scenarioId !== scenario.id
        ) {
          setError("The requested versions do not belong to this what-if.");
          return;
        }
        setComparison({ scenario, from, to, diff });
      })
      .catch((caught: unknown) => {
        if (active) {
          setError(caught instanceof Error ? caught.message : "Unable to compare versions");
        }
      });
    return () => {
      active = false;
    };
  }, [client, fromVersionId, scenarioId, toVersionId]);

  if (error !== undefined) {
    return (
      <DetailSurface eyebrow="Immutable history" title="Comparison unavailable" onBack={onBack}>
        <p className="error-banner" role="alert">
          {error}
        </p>
      </DetailSurface>
    );
  }
  if (comparison === undefined) {
    return (
      <DetailSurface
        eyebrow="Immutable history"
        title="Loading version comparison…"
        onBack={onBack}
      >
        <p role="status">Reading both pinned versions and their deterministic diff.</p>
      </DetailSurface>
    );
  }

  const { scenario, from, to, diff } = comparison;
  return (
    <div className="scenario-diff-view">
      <button className="back-button" type="button" onClick={onBack}>
        ← Back to what-if
      </button>
      <header className="page-heading-row">
        <div>
          <p className="eyebrow">Immutable what-if history</p>
          <h1>
            v{from.basis.versionNo} → v{to.basis.versionNo}
          </h1>
          <p>{scenario.currentVersion?.name ?? to.name}</p>
        </div>
      </header>

      <section className="panel scenario-diff-summary" aria-labelledby="diff-summary-heading">
        <div className="panel-heading">
          <div>
            <p className="section-kicker">Deterministic record comparison</p>
            <h2 id="diff-summary-heading">Exact changes</h2>
          </div>
          <span className="count-label">{new Date(diff.asOf).toLocaleString()}</span>
        </div>
        <VersionDiffSummary diff={diff} />
        <details>
          <summary>Why these rows are marked changed</summary>
          <p>
            Added, removed, and changed states come from canonical record comparison—not model
            narration. The two columns retain their original portfolio, ontology, run, and agent
            pins so a reviewer can trace the provenance of each revision.
          </p>
        </details>
      </section>

      <div className="scenario-diff-columns">
        <VersionColumn
          label={`Version ${from.basis.versionNo}`}
          projection={from}
          removed={new Set(diff.changes.nodesRemoved)}
          changed={new Set(diff.changes.nodesChanged)}
        />
        <VersionColumn
          label={`Version ${to.basis.versionNo}`}
          projection={to}
          added={new Set(diff.changes.nodesAdded)}
          changed={new Set(diff.changes.nodesChanged)}
        />
      </div>
    </div>
  );
}

function VersionColumn({
  label,
  projection,
  added = new Set<string>(),
  removed = new Set<string>(),
  changed = new Set<string>(),
}: {
  label: string;
  projection: MechanismProjection;
  added?: ReadonlySet<string>;
  removed?: ReadonlySet<string>;
  changed?: ReadonlySet<string>;
}) {
  return (
    <section className="panel scenario-version-column">
      <div className="panel-heading">
        <div>
          <p className="section-kicker">{projection.basis.createdAt}</p>
          <h2>{label}</h2>
        </div>
        <span className="evidence-state">
          {projection.basis.evidenceStatus.replaceAll("_", " ")}
        </span>
      </div>
      <h3>{projection.name}</h3>
      <p>{projection.thesis}</p>
      <ProvenanceDrawer
        title={`${label} provenance`}
        pins={[
          { label: "What-if version", value: projection.basis.scenarioVersionId },
          { label: "Portfolio version", value: projection.basis.bookVersionId },
          { label: "Ontology", value: projection.basis.ontologyVersionId },
          {
            label: "Agent manifest",
            value: projection.basis.agentManifestId ?? "Not model-produced",
          },
          ...(projection.basis.createdFromRunId === null
            ? []
            : [{ label: "Run", value: projection.basis.createdFromRunId }]),
        ]}
      />
      <ol className="scenario-version-path">
        {projection.nodes.map((node) => {
          const state = added.has(node.key)
            ? "added"
            : removed.has(node.key)
              ? "removed"
              : changed.has(node.key)
                ? "changed"
                : "unchanged";
          return (
            <li className={`diff-${state}`} key={node.key}>
              <span>{state}</span>
              <strong>{node.label}</strong>
              <small>
                {node.type.replaceAll("_", " ")} · {node.key}
              </small>
            </li>
          );
        })}
      </ol>
      <section className="scenario-version-impacts">
        <h3>Affected exposures</h3>
        {projection.impacts.length === 0 ? (
          <p>None recorded in this version.</p>
        ) : (
          <ul>
            {projection.impacts.map((impact) => (
              <li key={impact.positionId}>
                <strong>{impact.positionName}</strong>
                <span>
                  {impact.direction} · {impact.magnitude}
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>
    </section>
  );
}

function VersionDiffSummary({ diff }: { diff: MechanismVersionDiff }) {
  const changes = diff.changes;
  const entries = [
    ["Nodes added", changes.nodesAdded],
    ["Nodes removed", changes.nodesRemoved],
    ["Nodes changed", changes.nodesChanged],
    ["Edges added", changes.edgesAdded],
    ["Edges removed", changes.edgesRemoved],
    ["Impacts added", changes.impactsAdded],
    ["Impacts removed", changes.impactsRemoved],
  ] as const;
  return (
    <div>
      <dl className="detail-facts compact">
        <Fact label="Thesis changed" value={changes.thesisChanged ? "Yes" : "No"} />
        <Fact label="Portfolio pin changed" value={changes.bookVersionChanged ? "Yes" : "No"} />
      </dl>
      <ul className="version-diff-list">
        {entries.map(([label, values]) => (
          <li key={label}>
            <strong>{label}</strong>
            <span>{values.length === 0 ? "None" : values.join(", ")}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function EvidenceRail({
  title,
  status,
  links,
}: {
  title: string;
  status: MechanismProjection["basis"]["evidenceStatus"];
  links: MechanismProjection["evidenceRails"]["supports"];
}) {
  return (
    <section className="detail-section evidence-rail">
      <h2>{title}</h2>
      <span className="status-pill">{status.replaceAll("_", " ")}</span>
      {links.length === 0 ? (
        <p>No evidence contract is linked to this rail.</p>
      ) : (
        <ul>
          {links.map((link) => (
            <li key={link.id}>
              <strong>{link.contractName}</strong>
              <span>
                Node {link.nodeKey} · {link.evaluationState ?? "not observed"} · source{" "}
                {link.sourceHealth ?? "unavailable"}
              </span>
              <small>
                Independence group {link.independenceGroup} · reliability{" "}
                {Math.round(link.reliability * 100)}%
              </small>
              {link.lastObservationAt === null ? null : (
                <small>
                  Last observation {new Date(link.lastObservationAt).toLocaleString()}
                  {link.lastValue === null ? "" : ` · ${link.lastValue}`}
                </small>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

export function RunCenterView({
  data,
  client,
  role,
  busy,
  progress,
  onLaunch,
  onOpen,
}: {
  data: WorkspaceData;
  client: PolyfutureApiClient;
  role: CurrentUser["activeMembership"]["role"];
  busy: boolean;
  progress?: RunProgress | undefined;
  onLaunch: () => void;
  onOpen: (runId: string) => void;
}) {
  const [dataRuns, setDataRuns] = useState<readonly DataQueryRun[]>([]);
  const [dataRunsError, setDataRunsError] = useState<string>();
  useEffect(() => {
    let active = true;
    void client
      .listDataQueryRuns()
      .then((runs) => {
        if (active) {
          setDataRuns(runs);
          setDataRunsError(undefined);
        }
      })
      .catch((error: unknown) => {
        if (active) {
          setDataRunsError(error instanceof Error ? error.message : "Unable to load data runs");
        }
      });
    return () => {
      active = false;
    };
  }, [client]);
  return (
    <CollectionSurface
      eyebrow="Durable execution"
      title="Run centre"
      body="Every run is pinned to the current portfolio and configuration versions. Progress can reconnect from the durable event log."
      action={
        <button
          className="button button-primary"
          type="button"
          disabled={role === "viewer" || busy}
          onClick={onLaunch}
        >
          Start targeted run
        </button>
      }
    >
      {progress === undefined ? null : (
        <p className="run-progress-line" role="status">
          {progress.phase.replaceAll("_", " ")} · {progress.done} of {progress.total || "…"}
        </p>
      )}
      {data.runs.length === 0 ? (
        <EmptyCollection text="No what-if runs have been requested." />
      ) : (
        <div className="record-list">
          {data.runs.map((run) => (
            <button type="button" key={run.id} onClick={() => onOpen(run.id)}>
              <span>
                <strong>{run.kind} run</strong>
                <small>{new Date(run.createdAt).toISOString()}</small>
              </span>
              <span>
                {run.status} · ${run.costUsd}
              </span>
            </button>
          ))}
        </div>
      )}
      <section className="run-centre-data-queries" aria-labelledby="run-centre-data-title">
        <div className="panel-heading">
          <div>
            <p className="section-kicker">Governed provider execution</p>
            <h2 id="run-centre-data-title">Data queries</h2>
          </div>
          <span className="count-label">{dataRuns.length}</span>
        </div>
        <p className="trust-note">
          Provider, immutable catalog version, result volume, failure reason, and the authorized
          provider-cost ceiling are server truth. A provider cost is never invented when its API
          does not report one.
        </p>
        {dataRunsError === undefined ? null : <p role="alert">{dataRunsError}</p>}
        {dataRuns.length === 0 ? (
          <EmptyCollection text="No governed data queries have been requested." />
        ) : (
          <div className="record-list data-query-run-list">
            {dataRuns.map((run) => (
              <article key={run.id}>
                <span>
                  <strong>{run.commandId}</strong>
                  <small>
                    {run.providerKey} · catalog {run.catalogDigest.slice(0, 10)}… · attempt{" "}
                    {run.executionAttempt}
                  </small>
                </span>
                <span>
                  <strong>{run.status.replaceAll("_", " ")}</strong>
                  <small>
                    {run.resultRows.toLocaleString()} rows · {formatRunBytes(run.resultBytes)} ·
                    cost ceiling {formatProviderCostCeiling(run.budgets.maxProviderCostMicros)}
                  </small>
                  {run.error === undefined ? null : (
                    <small className="error-text">
                      {run.error.code}: {run.error.message}
                    </small>
                  )}
                </span>
              </article>
            ))}
          </div>
        )}
      </section>
    </CollectionSurface>
  );
}

function formatRunBytes(bytes: number): string {
  if (bytes < 1_024) return `${bytes} B`;
  if (bytes < 1_048_576) return `${(bytes / 1_024).toFixed(1)} KB`;
  return `${(bytes / 1_048_576).toFixed(1)} MB`;
}

function formatProviderCostCeiling(micros: number): string {
  return `$${(micros / 1_000_000).toFixed(4)}`;
}

export function RunDetailView({
  runId,
  client,
  onBack,
  onOpenScenario,
}: {
  runId: string;
  client: PolyfutureApiClient;
  onBack: (bookId: string) => void;
  onOpenScenario: (scenarioId: string) => void;
}) {
  const [run, setRun] = useState<WorkspaceData["runs"][number]>();
  const [error, setError] = useState<string>();
  const [connection, setConnection] = useState<"connecting" | "live" | "reconnecting" | "closed">(
    "connecting",
  );

  useEffect(() => {
    let active = true;
    setRun(undefined);
    setError(undefined);
    void client
      .getScenarioRun(runId)
      .then((record) => {
        if (active) setRun(record);
      })
      .catch((caught: unknown) => {
        if (active) setError(caught instanceof Error ? caught.message : "Unable to load run");
      });
    return () => {
      active = false;
    };
  }, [client, runId]);

  useEffect(() => {
    if (run === undefined) return;
    if (run.status === "complete" || run.status === "partial" || run.status === "failed") {
      setConnection("closed");
      return;
    }
    let active = true;
    const refresh = () => {
      void client
        .getScenarioRun(runId)
        .then((record) => {
          if (active) setRun(record);
        })
        .catch(() => undefined);
    };
    setConnection("connecting");
    const close = client.streamScenarioRun(runId, {
      onProgress: refresh,
      onScenarioCompleted: refresh,
      onScenarioFailed: refresh,
      onScenarioRetry: refresh,
      onConnectionOpen: () => setConnection("live"),
      onConnectionError: () => setConnection("reconnecting"),
      onCompleted: () => {
        setConnection("closed");
        refresh();
      },
    });
    return () => {
      active = false;
      close();
    };
  }, [client, run?.status, runId]);

  if (error !== undefined) {
    return (
      <section className="empty-state full" role="alert">
        <p className="eyebrow">RUN UNAVAILABLE</p>
        <h1>This run cannot be opened.</h1>
        <p>{error}</p>
      </section>
    );
  }
  if (run === undefined) {
    return <p role="status">Loading the durable run projection…</p>;
  }

  return (
    <div className="run-detail-view">
      <button className="back-button" type="button" onClick={() => onBack(run.bookId)}>
        ← Run centre
      </button>
      <header className="page-heading-row">
        <div>
          <p className="eyebrow">Durable execution</p>
          <h1>Run centre</h1>
          <p className="mono">{run.id}</p>
        </div>
      </header>
      <div className="run-truth-grid">
        <section>
          <span>Run status · server truth</span>
          <strong>{run.status.replaceAll("_", " ")}</strong>
          <p>Durable state in PostgreSQL. It survives this browser closing.</p>
        </section>
        <section>
          <span>Connection · this browser</span>
          <strong>{connectionLabel(connection)}</strong>
          <p>{connectionExplanation(connection)}</p>
        </section>
      </div>
      <section className="panel">
        <div className="panel-heading">
          <div>
            <p className="section-kicker">Immutable execution basis</p>
            <h2>Pinned inputs</h2>
          </div>
        </div>
        <dl className="detail-facts compact">
          <Fact label="Portfolio version" value={run.bookVersionId} />
          <Fact label="Effective configuration" value={run.effectiveConfigurationId} />
          <Fact label="Agent manifest" value={run.agentManifestId ?? "No model call recorded"} />
          <Fact label="Runtime run" value={run.runtimeRunId ?? "Not assigned"} />
          <Fact label="Requested by" value={run.requestedBy} />
          <Fact
            label="Archetype set"
            value={run.archetypeSetKey ?? "Pinned configuration default"}
          />
          <Fact
            label="Budget drawn"
            value={
              run.budget === undefined
                ? "Organization analysis budget"
                : `Organization analysis budget · $${run.budget.actualCostUsd}`
            }
          />
        </dl>
        <p className="trust-note">
          Cost is charged to the organization-scoped analysis budget enforced when work is admitted.
          This projection shows actual reconciled cost; it does not invent a separate budget id.
        </p>
      </section>
      <section className="panel">
        <div className="panel-heading">
          <div>
            <p className="section-kicker">Independent durable work</p>
            <h2>Tasks and redelivery</h2>
          </div>
          <span className="count-label">{run.tasks?.length ?? 0}</span>
        </div>
        <p className="trust-note">
          Each task completes or fails independently. A redelivery reuses the same what-if version
          and business identity; it is another attempt, not another analysis.
        </p>
        {run.tasks?.length ? (
          <div className="run-task-list">
            {run.tasks.map((task) => (
              <article key={task.scenarioVersionId}>
                <span className={`status-pill status-${task.status}`}>
                  {task.status.replaceAll("_", " ")}
                </span>
                <div>
                  <strong>{task.name}</strong>
                  <small>
                    Attempt {task.attempt || "not started"} · version{" "}
                    {abbreviateId(task.scenarioVersionId)}
                  </small>
                </div>
                <span>
                  {task.redeliveryCount === 0
                    ? "No redelivery"
                    : `${task.redeliveryCount} redeliver${task.redeliveryCount === 1 ? "y" : "ies"}`}
                </span>
              </article>
            ))}
          </div>
        ) : (
          <p className="empty-row">No independent task records are attached to this run.</p>
        )}
      </section>
      <section className="panel">
        <div className="panel-heading">
          <div>
            <p className="section-kicker">Independent outputs</p>
            <h2>What-ifs created by this run</h2>
          </div>
          <span className="count-label">{run.scenarios?.length ?? 0}</span>
        </div>
        {run.scenarios?.length ? (
          <div className="record-list">
            {run.scenarios.map((scenario) => (
              <button type="button" key={scenario.id} onClick={() => onOpenScenario(scenario.id)}>
                <span>
                  <strong>{scenario.currentVersion?.name ?? "Elaboration pending"}</strong>
                  <small>{scenario.currentVersion?.thesis ?? scenario.origin}</small>
                </span>
                <span>{scenario.status.replaceAll("_", " ")}</span>
              </button>
            ))}
          </div>
        ) : (
          <p className="empty-row">No what-if output is attached to this run.</p>
        )}
      </section>
      <section className="panel">
        <div className="panel-heading">
          <div>
            <p className="section-kicker">Replayable server history</p>
            <h2>Event and redelivery log</h2>
          </div>
          <span className="count-label">{run.events?.length ?? 0}</span>
        </div>
        {run.events?.length ? (
          <ol className="run-event-log">
            {run.events.map((event) => (
              <li key={event.id}>
                <time dateTime={event.at}>{new Date(event.at).toLocaleString()}</time>
                <strong>{event.event.replaceAll(".", " · ")}</strong>
                <span>{runEventSummary(event.data)}</span>
              </li>
            ))}
          </ol>
        ) : (
          <p className="empty-row">No public run events have been recorded.</p>
        )}
      </section>
    </div>
  );
}

function connectionLabel(state: "connecting" | "live" | "reconnecting" | "closed"): string {
  return {
    connecting: "Connecting",
    live: "Live",
    reconnecting: "Reconnecting",
    closed: "Closed · run terminal",
  }[state];
}

function connectionExplanation(state: "connecting" | "live" | "reconnecting" | "closed"): string {
  return {
    connecting: "Opening a resumable stream from the durable event log.",
    live: "New events are streaming to this browser; reconnects resume from the last event id.",
    reconnecting:
      "The browser stream is interrupted. Server-side work continues and events remain durable.",
    closed: "No live stream is required because the durable run is terminal.",
  }[state];
}

function runEventSummary(data: Record<string, unknown>): string {
  const scenario =
    typeof data["scenario_id"] === "string" ? abbreviateId(data["scenario_id"]) : undefined;
  const attempt = typeof data["attempt"] === "number" ? data["attempt"] : undefined;
  const phase = typeof data["phase"] === "string" ? productLabel(data["phase"]) : undefined;
  return (
    [
      scenario === undefined ? undefined : `what-if ${scenario}`,
      attempt === undefined ? undefined : `attempt ${attempt}`,
      phase,
    ]
      .filter((value): value is string => value !== undefined)
      .join(" · ") || "Durable state transition"
  );
}

function abbreviateId(value: string): string {
  return value.length > 12 ? `${value.slice(0, 8)}…${value.slice(-4)}` : value;
}

export function WhatIfLabView({
  data,
  role,
  busy,
  onCreate,
  onPromote,
  onRecordOutcome,
}: {
  data: WorkspaceData;
  role: CurrentUser["activeMembership"]["role"];
  busy: boolean;
  onCreate: (shock: string) => void;
  onPromote: (
    runId: string,
    input: { name: string; polarity: ScenarioPolarity; falsification: string },
  ) => Promise<boolean>;
  /** ADR-089: records whether a run's shock happened, so its judgments can be scored. */
  onRecordOutcome?: (
    runId: string,
    input: { happened: boolean; on?: string; notes: string },
  ) => Promise<void>;
}) {
  const [promotionId, setPromotionId] = useState<string>();
  const [promotionName, setPromotionName] = useState("");
  const [promotionPolarity, setPromotionPolarity] = useState<ScenarioPolarity>("negative");
  const [promotionFalsification, setPromotionFalsification] = useState("");
  const startPromotion = (run: WorkspaceData["whatIfs"][number]) => {
    setPromotionId(run.id);
    setPromotionName(run.shock.slice(0, 120));
    setPromotionPolarity("negative");
    setPromotionFalsification("");
  };
  const cancelPromotion = () => {
    setPromotionId(undefined);
    setPromotionName("");
    setPromotionFalsification("");
  };
  const submitPromotion = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (promotionId === undefined) return;
    const promoted = await onPromote(promotionId, {
      name: promotionName,
      polarity: promotionPolarity,
      falsification: promotionFalsification,
    });
    if (promoted) cancelPromotion();
  };

  return (
    <CollectionSurface
      eyebrow="Ephemeral analysis"
      title="Quick test"
      body="Explore a bounded shock without changing the published portfolio or promoting a what-if."
    >
      {role === "viewer" ? (
        <p className="permission-note">Analyst access is required to run a shock.</p>
      ) : (
        <WhatIfForm disabled={busy} onSubmit={onCreate} />
      )}
      {data.whatIfs.length === 0 ? (
        <EmptyCollection text="No what-if analyses have been requested." />
      ) : (
        <div className="quick-test-list">
          {data.whatIfs.map((run) => (
            <article key={run.id}>
              <div>
                <span className="status-pill">Ephemeral · not canonical</span>
                <span className={"status-pill status-" + run.status}>{run.status}</span>
              </div>
              <section>
                <strong>{run.shock}</strong>
                <p>{run.result?.netAssessment ?? run.result?.net_assessment ?? "Result pending"}</p>
                <small className="mono-note">
                  {run.bookVersionId === undefined
                    ? "Pinned portfolio version retained by server"
                    : "Pinned portfolio " + abbreviateId(run.bookVersionId)}
                  {" · $" + run.costUsd}
                </small>
              </section>
              {role !== "viewer" && run.status === "complete" ? (
                <button
                  className="button button-secondary"
                  type="button"
                  disabled={busy}
                  onClick={() => startPromotion(run)}
                >
                  Start governed promotion
                </button>
              ) : null}
              {role !== "viewer" && run.status === "complete" && onRecordOutcome !== undefined ? (
                <RecordOutcome
                  subject="shock"
                  onRecord={(input) => onRecordOutcome(run.id, input)}
                />
              ) : null}
            </article>
          ))}
        </div>
      )}
      {promotionId === undefined ? null : (
        <form className="quick-test-promotion" onSubmit={(event) => void submitPromotion(event)}>
          <div className="full-field">
            <p className="section-kicker">Governed boundary</p>
            <h2>Start a separate proposed what-if</h2>
            <p>
              The quick-test result remains immutable and outside the library. This creates a new
              draft with its own audit record and no model run; you can review it before submitting
              it for elaboration.
            </p>
          </div>
          <label>
            <span>Name</span>
            <input
              required
              maxLength={120}
              value={promotionName}
              onChange={(event) => setPromotionName(event.target.value)}
            />
          </label>
          <label>
            <span>Polarity</span>
            <select
              value={promotionPolarity}
              onChange={(event) => setPromotionPolarity(event.target.value as ScenarioPolarity)}
            >
              <option value="negative">Negative</option>
              <option value="positive">Positive</option>
              <option value="mixed">Mixed</option>
            </select>
          </label>
          <label className="full-field">
            <span>What would falsify this?</span>
            <input
              required
              maxLength={500}
              value={promotionFalsification}
              placeholder="State one observable condition on one line"
              onChange={(event) => setPromotionFalsification(event.target.value)}
            />
          </label>
          <div className="form-actions full-field">
            <button className="button button-primary" type="submit" disabled={busy}>
              {busy ? "Creating governed draft…" : "Create proposed what-if"}
            </button>
            <button className="button button-ghost" type="button" onClick={cancelPromotion}>
              Cancel
            </button>
          </div>
        </form>
      )}
    </CollectionSurface>
  );
}

function CollectionSurface({
  eyebrow,
  title,
  body,
  action,
  children,
}: {
  eyebrow: string;
  title: string;
  body: string;
  action?: ReactNode;
  children: ReactNode;
}) {
  return (
    <ProductPage className="book-collection-view">
      <ProductHeading
        eyebrow={eyebrow}
        title={title}
        body={<p>{body}</p>}
        {...(action === undefined ? {} : { actions: action })}
      />
      <div className="collection-content">{children}</div>
    </ProductPage>
  );
}

function DetailSurface({
  eyebrow,
  title,
  onBack,
  children,
}: {
  eyebrow: string;
  title: string;
  onBack: () => void;
  children: ReactNode;
}) {
  return (
    <div className="record-detail-view">
      <button className="back-button" type="button" onClick={onBack}>
        ← Back to list
      </button>
      <header className="page-heading-row">
        <div>
          <p className="eyebrow">{eyebrow}</p>
          <h1>{title}</h1>
        </div>
      </header>
      <div className="detail-content">{children}</div>
    </div>
  );
}

function Fact({ label, value }: { label: string; value: string | number }) {
  return (
    <div>
      <dt>{label}</dt>
      <dd>{value}</dd>
    </div>
  );
}

function pinnedBookVersion(book: BookRecord): string {
  const version =
    book.currentBookVersionNo === undefined ? "Draft" : `v${book.currentBookVersionNo}`;
  return book.currentBookVersionHash === undefined
    ? version
    : `${version} · ${book.currentBookVersionHash.slice(0, 10)}`;
}

function valuationBasis(values: Record<string, unknown> | undefined): string {
  if (values === undefined) return "Not stated";
  if (values["market_value"] !== undefined) return "Supplied market value";
  if (values["valuation_date"] !== undefined) return "Source valuation date";
  if (values["cost_basis"] !== undefined) return "Quantity and cost basis";
  if (values["notional"] !== undefined) return "Reported notional";
  if (values["quantity"] !== undefined) return "Reported quantity";
  return "Profile-declared attributes";
}

function formatTimestamp(value: string | undefined, fallback = "Not recorded"): string {
  if (value === undefined) return fallback;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return new Intl.DateTimeFormat("en", {
    year: "numeric",
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  }).format(parsed);
}

function formatMoney(value: number, currency: string): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
    maximumFractionDigits: value < 10 ? 4 : 2,
  }).format(value);
}

function toDateInput(value: string | undefined): string {
  if (value === undefined) return "";
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? "" : parsed.toISOString().slice(0, 10);
}

function humanizeKey(value: string): string {
  return value.replaceAll(/[_.:-]+/g, " ");
}

function formatAttribute(value: unknown): string {
  if (value === null) return "Not stated";
  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  return JSON.stringify(value);
}

function linkedEntityLabel(
  data: WorkspaceData,
  type: "position" | "assumption" | "scenario" | "evidence" | "action",
  id: string,
): string {
  if (type === "scenario") {
    return data.scenarios.find((scenario) => scenario.id === id)?.currentVersion?.name ?? id;
  }
  if (type === "position") {
    return data.positions.find((position) => position.id === id)?.name ?? id;
  }
  if (type === "assumption") {
    return data.assumptions.find((assumption) => assumption.id === id)?.text ?? id;
  }
  return id;
}

function summarizeImpactRange(projection: MechanismProjection): string {
  const minimums = projection.impacts.flatMap((impact) =>
    impact.magnitudePctMin === null ? [] : [impact.magnitudePctMin],
  );
  const maximums = projection.impacts.flatMap((impact) =>
    impact.magnitudePctMax === null ? [] : [impact.magnitudePctMax],
  );

  if (minimums.length === 0 && maximums.length === 0) {
    return projection.impacts.length === 0
      ? "No assessed impact recorded"
      : "Qualitative range only";
  }

  const lowerBound = Math.min(...(minimums.length === 0 ? maximums : minimums));
  const upperBound = Math.max(...(maximums.length === 0 ? minimums : maximums));
  return `${lowerBound.toFixed(1)}% to ${upperBound.toFixed(1)}%`;
}

function summarizeMatrixImpact(impacts: MechanismProjection["impacts"]): string {
  if (impacts.length === 0) return "No assessed path";
  const minimums = impacts.flatMap((impact) =>
    impact.magnitudePctMin === null ? [] : [impact.magnitudePctMin],
  );
  const maximums = impacts.flatMap((impact) =>
    impact.magnitudePctMax === null ? [] : [impact.magnitudePctMax],
  );
  if (minimums.length === 0 && maximums.length === 0) return "Qualitative only";
  const lowerBound = Math.min(...(minimums.length === 0 ? maximums : minimums));
  const upperBound = Math.max(...(maximums.length === 0 ? minimums : maximums));
  return `${formatSignedPercentage(lowerBound)} … ${formatSignedPercentage(upperBound)}`;
}

function formatImpactRange(impact: MechanismProjection["impacts"][number]): string {
  if (impact.magnitudePctMin === null && impact.magnitudePctMax === null) return impact.magnitude;
  const lowerBound = impact.magnitudePctMin ?? impact.magnitudePctMax ?? 0;
  const upperBound = impact.magnitudePctMax ?? impact.magnitudePctMin ?? 0;
  return `${formatSignedPercentage(lowerBound)} … ${formatSignedPercentage(upperBound)}`;
}

function formatSignedPercentage(value: number): string {
  return `${value > 0 ? "+" : ""}${value.toFixed(1)}%`;
}

function EmptyCollection({ text }: { text: string }) {
  return <div className="empty-state compact">{text}</div>;
}

function MissingRecord({ label, onBack }: { label: string; onBack: () => void }) {
  return (
    <section className="empty-state full" role="alert">
      <p className="eyebrow">RECORD UNAVAILABLE</p>
      <h1>This {label} cannot be opened.</h1>
      <p>It may have been retired or is not visible in the current governed portfolio version.</p>
      <button className="button" type="button" onClick={onBack}>
        Return to list
      </button>
    </section>
  );
}

function ScenarioForm({
  disabled,
  onSubmit,
}: {
  disabled: boolean;
  onSubmit: (input: ScenarioDraftInput) => void;
}) {
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    onSubmit({
      name: String(form.get("name") ?? ""),
      thesis: String(form.get("thesis") ?? ""),
      polarity: String(form.get("polarity") ?? "negative") as ScenarioPolarity,
      falsification: String(form.get("falsification") ?? ""),
    });
    event.currentTarget.reset();
  };
  return (
    <form className="inline-workflow-form" onSubmit={submit}>
      <input
        name="name"
        required
        maxLength={120}
        placeholder="What-if name"
        aria-label="What-if name"
      />
      <input
        name="thesis"
        required
        maxLength={200}
        placeholder="Stated thesis"
        aria-label="What-if thesis"
      />
      <select name="polarity" aria-label="What-if polarity" defaultValue="negative">
        <option value="negative">Negative</option>
        <option value="positive">Positive</option>
        <option value="mixed">Mixed</option>
      </select>
      <input
        name="falsification"
        required
        maxLength={500}
        placeholder="What would falsify this?"
        aria-label="What-if falsification"
      />
      <button className="button button-primary" type="submit" disabled={disabled}>
        Create what-if
      </button>
    </form>
  );
}

function WhatIfForm({
  disabled,
  onSubmit,
}: {
  disabled: boolean;
  onSubmit: (shock: string) => void;
}) {
  const [shock, setShock] = useState("");
  return (
    <form
      className="inline-workflow-form"
      onSubmit={(event) => {
        event.preventDefault();
        if (shock.trim().length === 0) return;
        onSubmit(shock.trim());
        setShock("");
      }}
    >
      <textarea
        value={shock}
        onChange={(event) => setShock(event.target.value)}
        required
        maxLength={400}
        placeholder="Describe a bounded shock"
        aria-label="What-if shock"
      />
      <button className="button button-primary" type="submit" disabled={disabled}>
        Run what-if
      </button>
    </form>
  );
}

function parentView(view: ActiveView): ActiveView {
  if (view === "position_detail") return "positions";
  if (view === "assumption_detail") return "assumptions";
  if (view === "scenario_detail") return "scenarios";
  if (view === "evidence_new") return "evidence";
  if (view === "evidence_detail") return "evidence";
  if (view === "investigation_detail") return "investigations";
  if (view === "imports") return "connect";
  return view;
}
